import type { StaticImageData } from 'next/image';
import type { FC } from 'react';
import Image from 'next/image';
import { cn } from '@/utils/cn';
import { createCloudflareLoader } from '@/utils/images/cloudflareLoader';

type ImageVariant
  = | 'game-cover-small'
    | 'game-cover-grid'
    | 'provider-cover'
    | 'bundle-cover'
    | 'gallery';

interface VariantConfig {
  width: number;
  height: number;
  sizes: string;
  objectFit?: 'cover' | 'contain';
}

const VARIANT_CONFIG: Record<ImageVariant, VariantConfig> = {
  'game-cover-small': {
    width: 120,
    height: 160,
    sizes: '120px',
    objectFit: 'cover',
  },
  'game-cover-grid': {
    width: 287,
    height: 383,
    sizes:
      '(max-width: 639px) 44vw, (max-width: 1023px) 30vw, (max-width: 1279px) 22vw, 120px',
    objectFit: 'cover',
  },
  // Provider logos: fit inside card without cropping
  'provider-cover': {
    width: 152,
    height: 137,
    sizes: '(max-width: 639px) 50vw, 152px',
    objectFit: 'contain',
  },
  // Bundle cards: artwork anchored to the right top, fixed visual size
  'bundle-cover': {
    width: 348,
    height: 148,
    sizes: '(max-width: 767px) 60vw, 348px',
    objectFit: 'contain',
  },
  // Game gallery thumbnails
  'gallery': {
    width: 400,
    height: 225,
    sizes: '400px',
    objectFit: 'cover',
  },
};

const getImageLoader = (config: VariantConfig, isSvg: boolean) => {
  if (isSvg) {
    return createCloudflareLoader({
      withoutTransformParams: true,
    });
  }

  if (config.objectFit === 'cover') {
    return createCloudflareLoader({
      fit: 'cover',
      width: config.width,
      height: config.height,
    });
  }

  return undefined;
};

export interface AppImageProps {
  src: string | StaticImageData;
  alt: string;
  variant: ImageVariant;
  mimeType?: string;
  priority?: boolean;
  className?: string;
}

export const AppImage: FC<AppImageProps> = ({
  src,
  alt,
  variant,
  mimeType,
  priority,
  className,
}) => {
  const config = VARIANT_CONFIG[variant];
  const normalizedMimeType = mimeType?.toLowerCase().split(';').at(0)?.trim();
  const isSvg = normalizedMimeType === 'image/svg+xml';
  const imageLoader = getImageLoader(config, isSvg);

  return (
    <Image
      src={src}
      alt={alt}
      width={config.width}
      height={config.height}
      sizes={config.sizes}
      priority={priority}
      loader={imageLoader}
      className={cn(
        'w-full h-full',
        config.objectFit === 'contain' ? 'object-contain' : 'object-cover',
        className,
      )}
    />
  );
};
